import { Input } from "./input"
import { Switch } from "./switch"

export type InputKind =
  | { kind: "int"; min?: number | null; max?: number | null; unit?: string | null }
  | { kind: "float"; min?: number | null; max?: number | null; min_exclusive?: boolean; max_exclusive?: boolean; unit?: string | null }
  | { kind: "bool" }
  | { kind: "string" }
  | { kind: "enum"; options: string[] }
  | { kind: "enum_set"; options: string[] }
  | { kind: "by_platform"; fields: Record<string, InputKind> }

export type InputValue = string | number | boolean | string[] | { [key: string]: InputValue } | null

interface KindInputProps {
  kind: InputKind
  value: InputValue
  onChange: (value: InputValue) => void
  id?: string
  placeholder?: string
  disabled?: boolean
  compact?: boolean
  "aria-invalid"?: boolean
  "aria-label"?: string
}

export function KindInput({ kind, value, onChange, id, placeholder, disabled, compact = false, "aria-invalid": invalid, "aria-label": label }: KindInputProps) {
  switch (kind.kind) {
    case "int":
    case "float":
      return (
        <span className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_4.5rem] items-center gap-2">
          <Input
            id={id}
            className={compact ? "h-7 min-w-0 px-2 font-mono text-xs" : "min-w-0"}
            type="number"
            step={kind.kind === "int" ? 1 : "any"}
            min={kind.min ?? undefined}
            max={kind.max ?? undefined}
            value={typeof value === "number" || typeof value === "string" ? value : ""}
            placeholder={placeholder}
            disabled={disabled}
            aria-label={label}
            aria-invalid={invalid}
            onChange={(event) => {
              const raw = event.currentTarget.value
              onChange(raw === "" ? null : Number(raw))
            }}
          />
          <span className="truncate text-muted-foreground text-xs" title={kind.unit ?? undefined}>{kind.unit}</span>
        </span>
      )
    case "bool":
      return (
        <span className="flex items-center gap-2">
          <Switch id={id} checked={value === true} disabled={disabled} aria-label={label} aria-invalid={invalid} onCheckedChange={(checked) => onChange(checked)} />
          <span className="text-muted-foreground text-xs">{value === null ? "Inherit" : value ? "On" : "Off"}</span>
        </span>
      )
    case "string":
      return <Input id={id} className={compact ? "h-7 px-2 text-xs" : undefined} value={typeof value === "string" ? value : ""} placeholder={placeholder} disabled={disabled} aria-label={label} aria-invalid={invalid} onChange={(event) => onChange(event.currentTarget.value || null)} />
    case "enum":
      return (
        <select id={id} className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" value={typeof value === "string" ? value : ""} disabled={disabled} aria-label={label} aria-invalid={invalid} onChange={(event) => onChange(event.currentTarget.value || null)}>
          <option value="">Inherit ({placeholder})</option>
          {kind.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      )
    case "enum_set":
      return (
        <div className="flex flex-wrap gap-3" id={id}>
          {kind.options.map((option) => (
            <label key={option} className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={Array.isArray(value) && value.includes(option)} disabled={disabled} aria-label={label ? `${label}: ${option}` : undefined} aria-invalid={invalid} onChange={(event) => {
                const selected = Array.isArray(value) ? value : []
                onChange(event.currentTarget.checked ? [...selected, option] : selected.filter((item) => item !== option))
              }} />
              {option}
            </label>
          ))}
        </div>
      )
    case "by_platform":
      return (
        <div className="grid gap-2" id={id}>
          {Object.entries(kind.fields).map(([platform, member]) => (
            <label key={platform} className="grid grid-cols-2 items-center gap-2 text-sm">
              <span>{platform.replaceAll("_", " ")}</span>
              <KindInput kind={member} value={value !== null && typeof value === "object" && !Array.isArray(value) ? value[platform] ?? null : null} onChange={(next) => {
                const current = value !== null && typeof value === "object" && !Array.isArray(value) ? value : {}
                onChange({ ...current, [platform]: next })
              }} disabled={disabled} />
            </label>
          ))}
        </div>
      )
  }
}
